import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { CurrentContextService } from '../../core/context/current-context.service';
import { liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { CatalogOffering, CatalogService, CatalogWarehouse, OutboundShipment } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-outbound-shipments',
  imports: [AinPageComponent, AinStateComponent, InventoryPageHintComponent, TableModule],
  template: `
    <ain-page [title]="t('catalog.integrations.outboundShipmentsTitle')" [description]="t('catalog.integrations.outboundShipmentsDescription')">
      <ain-inventory-page-hint pageKey="outboundShipments" />
      <span class="live-status" [class]="liveUpdateService.connectionState()">{{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}</span>
      @if (loading()) { <ain-state type="loading" [title]="t('catalog.loading')" /> }
      @else if (error()) {
        <ain-state type="error" [title]="t('catalog.integrations.loadFailed')" [message]="error()" />
      }
      @else {
        <p-table [value]="shipments()" [paginator]="true" [rows]="20">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('catalog.integrations.reference') }}</th>
              <th>{{ t('catalog.integrations.deliveryStatus') }}</th>
              <th>{{ t('catalog.integrations.carrier') }}</th>
              <th>{{ t('catalog.integrations.trackingNumber') }}</th>
              <th>{{ t('catalog.inventory.offeringId') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.referenceNumber }}</td>
              <td>{{ row.deliveryStatus }}</td>
              <td>{{ row.carrierName || row.carrierCode || '-' }}</td>
              <td>{{ row.trackingNumber || '-' }}</td>
              <td>{{ offeringLabel(row.offeringId) }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="5">{{ t('catalog.integrations.noShipments') }}</td></tr>
          </ng-template>
        </p-table>
      }
    </ain-page>
  `,
  styles: [`
    .live-status { font-size: 0.8rem; color: #6b7280; display: block; margin-bottom: 8px; }
    .live-status.connected { color: #166534; }
  `]
})
export class InventoryOutboundShipmentsComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;
  readonly loading = signal(true);
  readonly error = signal('');
  readonly shipments = signal<OutboundShipment[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  t = (k: string) => this.localization.translate(k);

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.reload(false)
    );
    this.service.listOfferings().subscribe({ next: rows => this.offerings.set(rows) });
    this.reload(true);
  }

  offeringLabel(offeringId: string): string {
    const offering = this.offerings().find(o => o.id === offeringId);
    return offering ? `${offering.code} - ${offering.title}` : offeringId;
  }

  reload(showSpinner = true): void {
    if (showSpinner) this.loading.set(true);
    this.error.set('');
    this.service.listOutboundShipments().subscribe({
      next: rows => { this.shipments.set(rows); this.loading.set(false); },
      error: () => {
        this.shipments.set([]);
        this.error.set(this.t('catalog.integrations.loadFailed'));
        this.loading.set(false);
      }
    });
  }
}
